import type { TranslationKey } from "@qualtio-workhub/i18n";
import {
  BarChart3,
  Bot,
  Boxes,
  CircleDot,
  ClipboardList,
  GitPullRequest,
  Inbox,
  Kanban,
  KeyRound,
  Layers3,
  type LucideIcon,
  Map as MapIcon,
  MessageSquare,
  PackageOpen,
  Radar,
  Route,
  UserCog,
  Users,
  Workflow,
  Wrench,
} from "lucide-react";

export type AppRoutePath =
  | "/"
  | "/my-work"
  | "/inbox"
  | "/triage"
  | "/issues"
  | "/board"
  | "/agents"
  | "/products"
  | "/clients"
  | "/projects"
  | "/research"
  | "/chapters"
  | "/cycles"
  | "/roadmap"
  | "/analytics"
  | "/settings/api-keys"
  | "/settings/users"
  | "/settings/maintenance"
  | "/settings/slack"
  | "/settings/github";

export type NavGroup = "work" | "agents" | "context" | "planning" | "settings";

export type NavItem = {
  path: Exclude<AppRoutePath, "/">;
  labelKey: TranslationKey;
  icon: LucideIcon;
  group: NavGroup;
  adminOnly?: boolean;
};

export const navItems: NavItem[] = [
  { path: "/my-work", labelKey: "nav.myWork", icon: CircleDot, group: "work" },
  { path: "/inbox", labelKey: "nav.inbox", icon: Inbox, group: "work" },
  { path: "/triage", labelKey: "nav.triage", icon: Radar, group: "work" },
  { path: "/issues", labelKey: "nav.issues", icon: ClipboardList, group: "work" },
  { path: "/board", labelKey: "nav.board", icon: Kanban, group: "work" },
  { path: "/agents", labelKey: "nav.agents", icon: Bot, group: "agents" },
  { path: "/products", labelKey: "nav.products", icon: PackageOpen, group: "context" },
  { path: "/clients", labelKey: "nav.clients", icon: Users, group: "context" },
  { path: "/projects", labelKey: "nav.projects", icon: Boxes, group: "context" },
  { path: "/research", labelKey: "nav.research", icon: Route, group: "context" },
  { path: "/chapters", labelKey: "nav.chapters", icon: Layers3, group: "context" },
  { path: "/cycles", labelKey: "nav.cycles", icon: Workflow, group: "planning" },
  { path: "/roadmap", labelKey: "nav.roadmap", icon: MapIcon, group: "planning" },
  { path: "/analytics", labelKey: "nav.analytics", icon: BarChart3, group: "planning" },
  { path: "/settings/api-keys", labelKey: "nav.apiKeys", icon: KeyRound, group: "settings" },
  {
    path: "/settings/users",
    labelKey: "nav.users",
    icon: UserCog,
    group: "settings",
    adminOnly: true,
  },
  {
    path: "/settings/maintenance",
    labelKey: "nav.maintenance",
    icon: Wrench,
    group: "settings",
    adminOnly: true,
  },
  {
    path: "/settings/slack",
    labelKey: "nav.slack",
    icon: MessageSquare,
    group: "settings",
    adminOnly: true,
  },
  {
    path: "/settings/github",
    labelKey: "nav.github",
    icon: GitPullRequest,
    group: "settings",
    adminOnly: true,
  },
];

export const navGroups: Array<{ id: NavGroup; labelKey: TranslationKey }> = [
  { id: "work", labelKey: "nav.group.work" },
  { id: "agents", labelKey: "nav.group.agents" },
  { id: "context", labelKey: "nav.group.context" },
  { id: "planning", labelKey: "nav.group.planning" },
  { id: "settings", labelKey: "nav.group.settings" },
];

export function getVisibleNavItems(role: "admin" | "member" | undefined): NavItem[] {
  return navItems.filter((item) => !item.adminOnly || role === "admin");
}
